import { Box, Divider, Stack, Tab, Tabs, Typography } from "@mui/material";
import { AnimatePresence, motion } from "motion/react";

import React, { FC, useEffect, useRef, useState } from "react";
import GlassContainer from "./GlassContainer";
import useHash from "@src/hooks/hash";
import { Colors } from "@src/gatsby-theme-material-ui-top-layout/colors-twentyfive";
import MarkdownWrapper from "../Markdown/MarkdownWrapper";
import {
  classLabels,
  ContainerHeader,
  PitClass,
  PitExample,
  PitSubCategory,
  rootContent,
  SectionHeader,
} from "@src/helpers/taxonomy";
import Example from "./example";
import Breadcrumbs from "./breadcrumbs";
import Subcategory from "./subcategory";
import { useIntersection } from "@src/utils";
import { useWebsiteStore } from "@src/hooks/useWebsiteStore";
import Class from "./class";

interface Props {
  currentContent?: string;
}

const Content: FC<Props> = ({}) => {
  const hash = useHash();
  const scrollRef = useRef<HTMLElement>();
  const [currentData, setCurrentData] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  const [currentTab, setCurrentTab] = useState(0);

  const examplesSectionRef = useRef<HTMLElement>();
  const subcategoriesSectionRef = useRef<HTMLElement>();
  const classesSectionRef = useRef<HTMLElement>();

  const examplesInViewport = useIntersection(examplesSectionRef, "-300px");
  const overviewInViewport = useIntersection(scrollRef, "200px");

  const setCurrentTaxonomyExample = useWebsiteStore(
    (state) => state.actions.setCurrentTaxonomyExample,
  );

  const isInView = () => {
    if (!scrollRef.current) return false;
    const rect = (scrollRef.current as HTMLDivElement).getBoundingClientRect();
    return (
      rect.top >= 0 &&
      rect.top <= (window.innerHeight || document.documentElement.clientHeight)
    );
  };

  /**
   * TODO: Improve this... it doesn't work in FF?
   */
  const executeScroll = () => {
    if (scrollRef && scrollRef?.current && !isInView()) {
      (scrollRef.current as HTMLDivElement).scrollIntoView({
        behavior: "smooth",
      });
    }
  };

  useEffect(() => {
    setTimeout(() => {
      if (
        hash &&
        hash?.trim() !== "" &&
        hash?.trim() !== "#" &&
        hash?.trim() !== "root"
      ) {
        const fetchData = async () => {
          setLoading(true);

          const response = await fetch(`/pit-data/${hash.trim()}.json`);
          const result = await response.json();

          if (result?.examples?.length > 0) {
            setCurrentTaxonomyExample(result.examples[0].id);
          } else {
            setCurrentTaxonomyExample("");
          }

          setLoading(false);
          setCurrentData(result);
          executeScroll();
        };

        fetchData().catch(console.error);
      } else {
        setLoading(false);
        setCurrentData(rootContent);
        executeScroll();
      }
    });
  }, [hash]);

  useEffect(() => {
    if (overviewInViewport) {
      setCurrentTab(0);
    }

    if (examplesInViewport) {
      setCurrentTab(1);
    }
  }, [examplesInViewport, overviewInViewport]);

  return (
    <Box
      sx={{
        fontFamily: "Inter",
        maxWidth: {
          xs: "90%",
          lg: "950px",
        },
        marginLeft: "auto",
        marginRight: "auto",
        paddingBottom: "100px",
        scrollMarginTop: {
          xs: "100px",
          sm: "200px",
        },
      }}
      ref={scrollRef}
    >
      <AnimatePresence mode="wait">
        <motion.div
          layout
          initial={{
            opacity: 0,
          }}
          animate={{
            opacity: 1,
          }}
          exit={{
            opacity: 0,
            top: "100px",
          }}
          transition={{
            duration: 0.4,
          }}
          layoutId="content"
          style={{
            color: "#FFFFFF",
          }}
          key={hash}
        >
          <Tabs
            value={currentTab}
            aria-label="Navigate category"
            sx={{
              div: {
                justifyContent: "center",
              },
              marginBottom: "35px",
              button: {
                fontSize: "18px",
                textTransform: "capitalize",
                "&.Mui-selected": {
                  color: Colors.PrimaryContrast,
                },
              },
              ".MuiTabs-indicator": {
                backgroundColor: Colors.InfoMain,
                height: "3px",
              },
            }}
          >
            <Tab
              label="Overview"
              value={0}
              onClick={() => {
                scrollRef?.current?.scrollIntoView({
                  behavior: "smooth",
                });
              }}
              data-testid="tab-overview"
              disableRipple={true}
            />
            {currentData?.examples?.length > 0 && (
              <Tab
                label={"Examples"}
                value={1}
                data-testid="tab-examples"
                onClick={() => {
                  examplesSectionRef?.current?.scrollIntoView({
                    behavior: "smooth",
                  });
                }}
                disableRipple={true}
              />
            )}
            {currentData?.subcategories?.length > 0 && (
              <Tab
                label={"Subcategories"}
                data-testid="tab-subcategories"
                value={1}
                onClick={() => {
                  subcategoriesSectionRef?.current?.scrollIntoView({
                    behavior: "smooth",
                  });
                }}
                disableRipple={true}
              />
            )}
            {currentData?.classes?.length > 0 && (
              <Tab
                label={"Classes"}
                data-testid="tab-classes"
                value={1}
                onClick={() => {
                  classesSectionRef?.current?.scrollIntoView({
                    behavior: "smooth",
                  });
                }}
                disableRipple={true}
              />
            )}
          </Tabs>

          <GlassContainer
            sx={{
              marginBottom: "20px",
            }}
          >
            <Stack
              sx={{
                gap: {
                  xs: "10px",
                  lg: "20px",
                },
              }}
            >
              <Stack
                sx={{
                  flexDirection: {
                    xs: "column-reverse",
                    lg: "row",
                  },
                  width: "100%",
                  gap: {
                    xs: "10px",
                    lg: "auto",
                  },
                }}
              >
                <Stack
                  sx={{
                    order: {
                      xs: 1,
                      lg: "initial",
                    },
                    flexGrow: 1,
                    gap: {
                      xs: "10px",
                      lg: "5px",
                    },
                    flexDirection: "column",
                  }}
                >
                  {(currentData?.breadcrumbs ?? []).length > 0 && (
                    <Box
                      sx={{
                        marginBottom: "10px",
                      }}
                    >
                      <Breadcrumbs
                        breadcrumbs={currentData?.breadcrumbs ?? []}
                      />
                    </Box>
                  )}

                  <Stack
                    sx={{
                      flexDirection: {
                        xs: "column",
                        lg: "row",
                      },
                      gap: "5px",
                    }}
                  >
                    <ContainerHeader
                      variant="h1"
                      sx={{
                        background:
                          "linear-gradient(90deg, #FFCDAF 12.35%, #8C48EB 88.07%)",
                        backgroundClip: "text",
                        textFillColor: "transparent",
                        fontSize: {
                          xs: "20px",
                          lg: "initial",
                        },
                        lineHeight: {
                          xs: "22px",
                          lg: "initial",
                        },
                      }}
                    >
                      {currentData?.category}
                    </ContainerHeader>

                    {currentData?.class &&
                      currentData?.class?.trim() !== "" && (
                        <ContainerHeader
                          variant="h1"
                          sx={{
                            fontSize: {
                              xs: "20px",
                              lg: "initial",
                            },
                            lineHeight: {
                              xs: "22px",
                              lg: "initial",
                            },
                          }}
                        >
                          ({currentData?.id})
                        </ContainerHeader>
                      )}
                  </Stack>
                </Stack>

                {currentData?.class && currentData?.class?.trim() !== "" && (
                  <Box
                    sx={{
                      backgroundColor: "#8936C9",
                      borderRadius: "100px",
                      padding: "10px",
                      alignSelf: "center",
                      order: {
                        xs: 0,
                        lg: "initial",
                      },
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: "Source Code Pro",
                        fontSize: {
                          xs: "12px",
                          md: "14px",
                        },
                        fontWeight: 500,
                        lineHeight: 1,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {classLabels?.[currentData?.class] ?? ""}
                    </Typography>
                  </Box>
                )}
              </Stack>

              {((currentData?.description &&
                currentData?.description?.trim() !== "") ||
                (currentData?.mechanism &&
                  currentData?.mechanism?.trim() !== "") ||
                (currentData?.notes && currentData?.notes?.trim()) ||
                currentData?.references) && (
                <Divider
                  sx={{
                    borderColor: Colors.PrimaryContrast,
                    opacity: 0.4,
                  }}
                />
              )}

              {currentData?.description &&
                currentData?.description?.trim() !== "" && (
                  <Box>
                    {currentData?.id !== "root" && (
                      <SectionHeader>Description</SectionHeader>
                    )}

                    <MarkdownWrapper content={currentData?.description} />
                  </Box>
                )}

              {currentData?.mechanism &&
                currentData?.mechanism?.trim() !== "" && (
                  <Box>
                    <SectionHeader>Mechanism</SectionHeader>

                    <MarkdownWrapper content={currentData?.mechanism} />
                  </Box>
                )}

              {currentData?.notes && currentData?.notes?.trim() !== "" && (
                <Box>
                  <SectionHeader>Notes</SectionHeader>

                  <MarkdownWrapper content={currentData?.notes} />
                </Box>
              )}

              {currentData?.references &&
                currentData?.references?.trim() !== "" && (
                  <Box>
                    <SectionHeader>References</SectionHeader>

                    <MarkdownWrapper content={currentData?.references} />
                  </Box>
                )}
            </Stack>
          </GlassContainer>

          {currentData?.classes?.length > 0 && (
            <GlassContainer
              ref={classesSectionRef}
              sx={{
                scrollMarginTop: {
                  xs: "100px",
                  sm: "200px",
                },
              }}
            >
              <Stack
                data-testid="content-class"
                sx={{
                  gap: "20px",
                }}
              >
                <ContainerHeader
                  variant="h1"
                  sx={{
                    color: Colors.PrimaryContrast,
                    fontSize: {
                      xs: "20px",
                      lg: "initial",
                    },
                    lineHeight: {
                      xs: "22px",
                      lg: "initial",
                    },
                  }}
                >
                  Classes of PI Attack Methods
                </ContainerHeader>

                {currentData?.classes.map((pitClass: PitClass) => {
                  return <Class pitClass={pitClass} key={pitClass.id} />;
                })}
              </Stack>
            </GlassContainer>
          )}

          {currentData?.examples?.length > 0 && (
            <GlassContainer
              ref={examplesSectionRef}
              sx={{
                scrollMarginTop: {
                  xs: "100px",
                  sm: "200px",
                },
                marginBottom: "20px",
              }}
            >
              <Stack
                data-testid="content-examples"
                sx={{
                  gap: "20px",
                }}
              >
                <ContainerHeader
                  variant="h1"
                  sx={{
                    color: Colors.PrimaryContrast,
                    fontSize: {
                      xs: "20px",
                      lg: "initial",
                    },
                    lineHeight: {
                      xs: "22px",
                      lg: "initial",
                    },
                  }}
                >
                  Examples
                </ContainerHeader>

                {currentData?.examples.map(
                  (example: PitExample, exampleIndex: number) => {
                    return <Example example={example} key={example.id} />;
                  },
                )}
              </Stack>
            </GlassContainer>
          )}

          {currentData?.subcategories?.length > 0 && (
            <GlassContainer
              ref={subcategoriesSectionRef}
              sx={{
                scrollMarginTop: {
                  xs: "100px",
                  sm: "200px",
                },
              }}
            >
              <Stack
                data-testid="content-subcategories"
                sx={{
                  gap: "20px",
                }}
              >
                <ContainerHeader
                  variant="h1"
                  sx={{
                    color: Colors.PrimaryContrast,
                    fontSize: {
                      xs: "20px",
                      lg: "initial",
                    },
                    lineHeight: {
                      xs: "22px",
                      lg: "initial",
                    },
                  }}
                >
                  Subcategories
                </ContainerHeader>

                {currentData?.subcategories.map((category: PitSubCategory) => {
                  return (
                    <Subcategory
                      category={category}
                      key={category.categoryId}
                    />
                  );
                })}
              </Stack>
            </GlassContainer>
          )}
        </motion.div>
      </AnimatePresence>
    </Box>
  );
};

export default Content;
